import { jsPDF } from "jspdf";
import QRCode from "qrcode";
import JsBarcode from "jsbarcode";
import { LOGO_URL, DOCUMENT_TYPES } from "./constants";

// Linear (CODE128) barcode encoding the ACK number — synchronous, rendered
// via an offscreen canvas, unlike the QR generation which is async.
const generateBarcodeDataUrl = (value) => {
  if (!value) return null;
  try {
    const canvas = document.createElement("canvas");
    JsBarcode(canvas, String(value), { format: "CODE128", displayValue: false, margin: 0, height: 40 });
    return canvas.toDataURL("image/png");
  } catch (e) {
    return null;
  }
};

// ---- Logo loader (cached) ----
let _logoData = null;
let _logoPromise = null;
const loadLogo = () => {
  if (_logoData) return Promise.resolve(_logoData);
  if (_logoPromise) return _logoPromise;
  _logoPromise = fetch(LOGO_URL)
    .then((r) => r.blob())
    .then(
      (b) =>
        new Promise((res) => {
          const fr = new FileReader();
          fr.onload = () => {
            _logoData = fr.result;
            res(_logoData);
          };
          fr.onerror = () => res(null);
          fr.readAsDataURL(b);
        })
    )
    .catch(() => null);
  return _logoPromise;
};

// Format a JS Date / ISO string to "DD/MM/YYYY" (per user preference, e.g. 13/07/2026)
const formatShortDate = (d) => {
  if (!d) return "";
  // If it's already a "DD/MM/YYYY"-ish string, pass through.
  if (typeof d === "string" && /^\d{2}\/\d{2}\/\d{4}$/.test(d)) return d;
  const dt = typeof d === "string" ? new Date(d) : d;
  if (Number.isNaN(dt.getTime())) return String(d);
  const dd = String(dt.getDate()).padStart(2, "0");
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${dt.getFullYear()}`;
};

// Long form for the main statement body (e.g. "15 February 2026")
const formatLongDate = (d) => {
  if (!d) return "";
  const dt = typeof d === "string" ? new Date(d) : d;
  if (Number.isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
};

const drawLogoAt = (doc, dataUrl, x, y, maxW, maxH) => {
  if (!dataUrl) return;
  try {
    const p = doc.getImageProperties(dataUrl);
    const ratio = p.width / p.height;
    let w = maxW;
    let h = w / ratio;
    if (h > maxH) { h = maxH; w = h * ratio; }
    doc.addImage(dataUrl, "PNG", x + (maxW - w) / 2, y + (maxH - h) / 2, w, h);
  } catch (_e) { /* ignore */ }
};

// Render a paragraph where each segment is a plain string (normal weight) or
// { v: "value" } (rendered bold) — wraps to maxWidth. Returns the new y.
const writeMixedParagraph = (doc, segments, x, y, maxWidth, { fontSize = 11, lineHeight = 14 } = {}) => {
  doc.setFontSize(fontSize);
  const words = [];
  segments.forEach((seg) => {
    const text = typeof seg === "string" ? seg : String(seg.v ?? "");
    const bold = typeof seg !== "string";
    text.split(/(\s+)/).forEach((piece) => {
      if (!piece) return;
      if (/^\s+$/.test(piece)) words.push({ w: " ", bold, isSpace: true });
      else words.push({ w: piece, bold, isSpace: false });
    });
  });
  let cx = x, cy = y;
  const spaceW = (bold) => { doc.setFont("times", bold ? "bold" : "normal"); return doc.getTextWidth(" "); };
  words.forEach((word) => {
    doc.setFont("times", word.bold ? "bold" : "normal");
    const w = doc.getTextWidth(word.w);
    if (!word.isSpace && cx + w > x + maxWidth) { cy += lineHeight; cx = x; }
    if (word.isSpace) {
      if (cx === x) return;
      cx += spaceW(word.bold);
      return;
    }
    doc.text(word.w, cx, cy);
    cx += w;
  });
  return cy + lineHeight;
};

const dash = (v) => (v === 0 || v ? String(v) : "—");

/** Draw room-details table. Returns new y. */
const drawHostelAllotmentTable = (doc, hostel, x, y, maxWidth) => {
  const h = hostel || {};
  const rows = [
    ["Hostel", h.hostel_name ? `${h.hostel_name}${h.hostel_code ? ` (${h.hostel_code})` : ""}` : "—"],
    ["Room No", dash(h.room_number)],
    ["Floor", dash(h.floor)],
    ["Bed", dash(h.bed_letter)],
    ["Room Type", dash(h.room_type)],
    ["Allotment Status", dash(h.hostel_status === "reserved" ? "Reserved" : h.hostel_status)],
  ];
  const hasAllotment = !!(h.hostel_name || h.room_number || h.bed_letter);

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  const head = "ALLOTTED HOSTEL DETAILS";
  doc.text(head, x + maxWidth / 2, y, { align: "center" });
  const hw = doc.getTextWidth(head);
  doc.setLineWidth(0.7);
  doc.line(x + maxWidth / 2 - hw / 2, y + 2, x + maxWidth / 2 + hw / 2, y + 2);
  y += 14;

  if (!hasAllotment) {
    doc.setFont("times", "italic");
    doc.setFontSize(10);
    doc.text("Hostel allotment pending — details will be updated after room allocation.", x, y);
    return y + 14;
  }

  const labelW = 110;
  const rowH = 16;
  doc.setDrawColor(80);
  doc.setLineWidth(0.5);
  rows.forEach(([label, value]) => {
    doc.rect(x, y, maxWidth, rowH);
    doc.line(x + labelW, y, x + labelW, y + rowH);
    doc.setFont("times", "bold");
    doc.setFontSize(10);
    doc.text(label, x + 8, y + 11);
    doc.setFont("times", "normal");
    doc.text(value, x + labelW + 8, y + 11);
    y += rowH;
  });
  return y + 8;
};

/** Draw check-in QR at lower-right with a white backing so it stays visible on print. */
const drawHostelCheckinQr = (doc, topY, halfH, W, M, hostelCheckinQrDataUrl) => {
  if (!hostelCheckinQrDataUrl) return;
  const qrSize = 72;
  const qrX = W - M - qrSize - 4;
  const slipBottom = topY + halfH - 10;
  const qrY = slipBottom - qrSize - 14;
  try {
    doc.setFillColor(255, 255, 255);
    doc.rect(qrX - 2, qrY - 2, qrSize + 4, qrSize + 18, "F");
    doc.addImage(hostelCheckinQrDataUrl, "PNG", qrX, qrY, qrSize, qrSize);
    doc.setFont("times", "italic");
    doc.setFontSize(8);
    doc.setTextColor(0x6B, 0x72, 0x80);
    doc.text("Scan to check in", qrX + qrSize / 2, qrY + qrSize + 10, { align: "center" });
    doc.setTextColor(0);
  } catch (_e) {
    /* ignore bad image data */
  }
};

/**
 * One half of the tear-off hostel slip (top or bottom of the page).
 * No document list — allotment statement + room table only.
 */
const drawHostelSlipHalf = (doc, opts, topY, halfH) => {
  const {
    logo, candidateName, applicationNumber, rollNumber, ackNumber, receivedOn, hostel, slipLabel, schoolName,
    hostelCheckinQrDataUrl,
  } = opts;
  const W = doc.internal.pageSize.getWidth();
  const M = 36;
  const contentW = W - 2 * M;

  // Light border around this half so tear-off regions are clear
  doc.setDrawColor(0xC0, 0xC0, 0xC0);
  doc.setLineWidth(0.6);
  doc.rect(M - 8, topY + 6, contentW + 16, halfH - 16);

  // Rotated stamp
  doc.setFont("times", "bold");
  doc.setFontSize(12);
  doc.setTextColor(0x6B, 0x72, 0x80);
  doc.text("HOSTEL COPY", M + 4, topY + 42, { angle: 12 });
  doc.setTextColor(0);

  drawLogoAt(doc, logo, W - M - 52, topY + 12, 52, 40);

  let y = topY + 28;
  doc.setFont("times", "bold");
  doc.setFontSize(14);
  doc.text("MAHINDRA UNIVERSITY", W / 2, y, { align: "center" }); y += 13;
  doc.setFont("times", "normal");
  // Stay clear of the logo in the top-right corner for a long school name.
  const hostelSubtitle = `${(schoolName || "SCHOOL OF MANAGEMENT").toUpperCase()} - UNDER GRADUATE`;
  let hostelSubtitleSize = 9.5;
  doc.setFontSize(hostelSubtitleSize);
  const maxHostelSubtitleWidth = W - 184;
  while (doc.getTextWidth(hostelSubtitle) > maxHostelSubtitleWidth && hostelSubtitleSize > 7) {
    hostelSubtitleSize -= 0.5;
    doc.setFontSize(hostelSubtitleSize);
  }
  doc.text(hostelSubtitle, W / 2, y, { align: "center" }); y += 12;
  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("HOSTEL ROOM ALLOTMENT", W / 2, y, { align: "center" });
  const titleW = doc.getTextWidth("HOSTEL ROOM ALLOTMENT");
  doc.setLineWidth(0.7);
  doc.line(W / 2 - titleW / 2, y + 2, W / 2 + titleW / 2, y + 2);
  y += 14;

  if (slipLabel) {
    doc.setFont("times", "italic");
    doc.setFontSize(9);
    doc.setTextColor(0x6B, 0x72, 0x80);
    doc.text(slipLabel, W / 2, y, { align: "center" });
    doc.setTextColor(0);
    y += 12;
  }

  const statementSegs = [
    "This is to confirm that a hostel room has been allotted to ",
    { v: candidateName },
    ` (Application No: ${applicationNumber}`,
  ];
  if (rollNumber) { statementSegs.push(" / Roll No: "); statementSegs.push({ v: rollNumber }); }
  statementSegs.push(`${ackNumber ? " / ACK No: " + ackNumber : ""}) on ${formatLongDate(receivedOn)}.`);
  y = writeMixedParagraph(doc, statementSegs, M, y, contentW, { fontSize: 10, lineHeight: 12 });
  y += 8;

  y = drawHostelAllotmentTable(doc, hostel, M, y, contentW);

  // Signature row
  doc.setFont("times", "bold");
  doc.setFontSize(10);
  doc.text("Officer Signature", M, y);
  const sigLabelW = doc.getTextWidth("Officer Signature");
  doc.setFont("times", "normal");
  doc.line(M + sigLabelW + 8, y + 1, M + 220, y + 1);
  doc.setFont("times", "bold");
  doc.text("Date", M + 250, y);
  doc.setFont("times", "normal");
  doc.text(formatShortDate(receivedOn), M + 282, y);
  doc.line(M + 278, y + 1, M + 380, y + 1);

  // Check-in QR — lower-right of each slip half (drawn last so nothing covers it).
  drawHostelCheckinQr(doc, topY, halfH, W, M, hostelCheckinQrDataUrl);
};

/** Full A4 page: two identical hostel slips (top + bottom) with a tear line. */
const drawHostelTearOffPage = (doc, opts) => {
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const halfH = H / 2;

  drawHostelSlipHalf(doc, { ...opts, slipLabel: "Student / Parent copy" }, 0, halfH);

  // Tear / cut line down the middle
  const midY = halfH;
  doc.setDrawColor(0x6B, 0x72, 0x80);
  doc.setLineWidth(0.8);
  doc.setLineDashPattern([3, 3], 0);
  doc.line(24, midY, W - 24, midY);
  doc.setLineDashPattern([], 0);
  doc.setFont("times", "italic");
  doc.setFontSize(8);
  doc.setTextColor(0x6B, 0x72, 0x80);
  doc.text("✂  Cut / tear along this line  ✂", W / 2, midY - 4, { align: "center" });
  doc.setTextColor(0);

  drawHostelSlipHalf(doc, { ...opts, slipLabel: "Hostel office copy" }, halfH, halfH);
};

// Draw a page of the copy. `copyType` is "STUDENT COPY" or "OFFICE COPY".
const drawCopyPage = async (doc, opts) => {
  const {
    copyType, logo, qrDataUrl, candidateName, applicationNumber, rollNumber, ackNumber, receivedOn,
    docs, // [{ index, label, checked }]
    schoolName,
  } = opts;

  const W = doc.internal.pageSize.getWidth();
  const M = 40;
  const isStudentCopy = copyType === "STUDENT COPY";

  // "STUDENT COPY" / "OFFICE COPY" stamp at top-left (rotated)
  doc.setFont("times", "bold");
  doc.setFontSize(15);
  doc.setTextColor(0x6B, 0x72, 0x80);
  doc.text(copyType, 60, 76, { angle: 12 });
  doc.setTextColor(0);

  // Mahindra logo top-right
  drawLogoAt(doc, logo, W - M - 68, 22, 68, 54);

  // QR code (below logo)
  if (qrDataUrl) {
    const qrSize = 80;
    const qrX = W - M - qrSize - 4;
    const qrY = 80;
    doc.addImage(qrDataUrl, "PNG", qrX, qrY, qrSize, qrSize);
    doc.setFont("times", "normal");
    doc.setFontSize(7);
    doc.setTextColor(0x6B, 0x72, 0x80);
    doc.text("Scan to verify", qrX + qrSize / 2, qrY + qrSize + 8, { align: "center" });
    doc.setTextColor(0);
  }

  // Title block
  let y = 78;
  doc.setFont("times", "bold");
  doc.setFontSize(18);
  doc.text("MAHINDRA UNIVERSITY", W / 2, y, { align: "center" }); y += 18;
  doc.setFont("times", "normal");
  // Centered on the full page width, so it has to stay clear of the QR code
  // in the top-right corner — shrink to fit instead of overlapping for a
  // longer school name (multiple schools now, not just a fixed short string).
  const subtitle = `${(schoolName || "SCHOOL OF MANAGEMENT").toUpperCase()} - UNDER GRADUATE`;
  let subtitleSize = 12;
  doc.setFontSize(subtitleSize);
  const maxSubtitleWidth = W - 248;
  while (doc.getTextWidth(subtitle) > maxSubtitleWidth && subtitleSize > 8) {
    subtitleSize -= 0.5;
    doc.setFontSize(subtitleSize);
  }
  doc.text(subtitle, W / 2, y, { align: "center" }); y += 18;
  doc.setFont("times", "bold");
  doc.setFontSize(14);
  doc.text("REGISTRATION & ADMISSIONS", W / 2, y, { align: "center" }); y += 20;
  doc.setFont("times", "normal");
  doc.setFontSize(11);
  doc.text("ADMISSION YEAR - 2026", W / 2, y, { align: "center" }); y += 26;
  doc.setFont("times", "bold");
  doc.setFontSize(13);
  // Underlined "ACKNOWLEDGEMENT"
  const ackText = "ACKNOWLEDGEMENT";
  doc.text(ackText, W / 2, y, { align: "center" });
  const ackW = doc.getTextWidth(ackText);
  doc.setLineWidth(0.8);
  doc.line(W / 2 - ackW / 2, y + 2, W / 2 + ackW / 2, y + 2);
  y += 26;

  // Body statement — candidate name and roll number rendered bold for emphasis.
  const statementSegs = [
    "This is to acknowledge that Mahindra University has received below certificates in ORIGINALS from ",
    { v: candidateName },
    ` (Application No: ${applicationNumber}`,
  ];
  if (rollNumber) { statementSegs.push(" / Roll No: "); statementSegs.push({ v: rollNumber }); }
  statementSegs.push(`${ackNumber ? " / ACK No: " + ackNumber : ""}) on ${formatLongDate(receivedOn)}.`);
  y = writeMixedParagraph(doc, statementSegs, M, y, W - 2 * M, { fontSize: 11, lineHeight: 14 });
  y += 14;

  // Sub-heading
  doc.setFont("times", "bold");
  doc.setFontSize(12);
  const subHead = "TO BE FILLED BY THE VERIFYING OFFICER ONLY";
  doc.text(subHead, W / 2, y, { align: "center" });
  const shW = doc.getTextWidth(subHead);
  doc.line(W / 2 - shW / 2, y + 2, W / 2 + shW / 2, y + 2);
  y += 18;

  // Documents table
  const col1 = 32;               // # width
  const col3 = 90;               // In Original column width
  const col2 = W - 2 * M - col1 - col3; // Nature of Document
  const tableX = M;
  const rowH = 22;

  // Header row
  doc.setDrawColor(80);
  doc.setLineWidth(0.5);
  doc.setFillColor(245, 245, 245);
  doc.rect(tableX, y, W - 2 * M, rowH, "FD");
  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("#", tableX + col1 / 2, y + 14, { align: "center" });
  doc.text("Nature of Document", tableX + col1 + col2 / 2, y + 14, { align: "center" });
  doc.text("In Original", tableX + col1 + col2 + col3 / 2, y + 14, { align: "center" });
  // Vertical dividers on header
  doc.line(tableX + col1, y, tableX + col1, y + rowH);
  doc.line(tableX + col1 + col2, y, tableX + col1 + col2, y + rowH);
  y += rowH;

  // Body rows
  doc.setFont("times", "normal");
  doc.setFontSize(10.5);
  docs.forEach((d, i) => {
    // Row rectangle
    doc.rect(tableX, y, W - 2 * M, rowH);
    doc.line(tableX + col1, y, tableX + col1, y + rowH);
    doc.line(tableX + col1 + col2, y, tableX + col1 + col2, y + rowH);
    // Index
    doc.text(String(i + 1), tableX + col1 / 2, y + 14, { align: "center" });
    // Label
    doc.text(d.label, tableX + col1 + 8, y + 14);
    // Checkbox (drawn) — pre-ticked if admin marked as originalReceived
    const boxSize = 12;
    const boxX = tableX + col1 + col2 + (col3 - boxSize) / 2;
    const boxY = y + (rowH - boxSize) / 2;
    doc.setLineWidth(0.8);
    doc.rect(boxX, boxY, boxSize, boxSize);
    if (d.checked) {
      doc.setDrawColor(0x2E, 0x7D, 0x32);
      doc.setLineWidth(1.6);
      doc.line(boxX + 2, boxY + 6, boxX + 5, boxY + 10);
      doc.line(boxX + 5, boxY + 10, boxX + 11, boxY + 2);
      doc.setDrawColor(0);
      doc.setLineWidth(0.8);
    }
    y += rowH;
  });

  y += 22;
  // Footer note — moved down with more breathing room from the table above.
  doc.setFont("times", "italic");
  doc.setFontSize(10);
  doc.text("(To be signed in the presence of verifying officer only)", W / 2, y, { align: "center" }); y += 20;

  // Declaration + signature block differ by copy: the Student Copy is the
  // officer's receipt handed to the candidate (officer confirms + signs);
  // the Office Copy is the candidate's acknowledgement kept by the university
  // (candidate confirms + signs).
  const confirm = isStudentCopy
    ? 'I hereby confirm that I have received the documents tick-marked under the "In Original" column only.'
    : 'I hereby confirm that I have submitted the documents tick-marked under the "In Original" column only. I understand and acknowledge that all my original documents, except the Transfer Certificate (TC) and Migration Certificate, may be collected at any time after 31 October 2026 from Registration Office.';
  const signatureLabel = isStudentCopy ? "Officer Signature" : "Signature of the Candidate";

  doc.setFont("times", "normal");
  doc.setFontSize(10.5);
  const confirmLines = doc.splitTextToSize(confirm, W - 2 * M);
  doc.text(confirmLines, M, y);
  y += confirmLines.length * 13 + 34;

  // Signature + Date + Stamp placeholder
  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text(signatureLabel, M, y);
  const sigLabelW = doc.getTextWidth(signatureLabel);
  doc.setFont("times", "normal");
  doc.line(M + sigLabelW + 10, y + 2, M + 280, y + 2);
  doc.setFont("times", "bold");
  doc.text("Date", M + 320, y);
  doc.setFont("times", "normal");
  // Date shown = scheduled verification day (assigned slot), not "today".
  const dateStr = formatShortDate(receivedOn);
  doc.text(dateStr, M + 360, y);
  doc.line(M + 355, y + 2, M + 460, y + 2);
  y += 34;

  if (isStudentCopy) {
    // Reminder note — the student must bring this copy back when collecting originals.
    const noteH = 30;
    doc.setDrawColor(0x6B, 0x72, 0x80);
    doc.setLineWidth(0.5);
    doc.roundedRect(M, y, W - 2 * M, noteH, 3, 3, "S");
    doc.setFont("times", "bold"); doc.setFontSize(10);
    doc.text("Please note", M + 8, y + 11);
    doc.setFont("times", "normal"); doc.setFontSize(9.5);
    const keepLines = doc.splitTextToSize(
      "Please keep this copy safely — you will need to submit this copy when collecting your original documents.",
      W - 2 * M - 16
    );
    let ny = y + 22;
    keepLines.forEach((l) => { doc.text(l, M + 8, ny); ny += 12; });
    y += noteH + 14;
  }

  // Rounded stamp placeholder (bottom-right)
  const stampR = 46;
  const stampX = W - M - stampR - 8;
  const stampY = y + 20;
  doc.setDrawColor(0x99, 0x99, 0x99);
  doc.setLineWidth(1.2);
  doc.setLineDashPattern([3, 3], 0);
  doc.circle(stampX, stampY, stampR);
  doc.setLineDashPattern([], 0);
  doc.setFont("times", "italic");
  doc.setFontSize(9);
  doc.setTextColor(0x9C, 0xA3, 0xAF);
  doc.text("Office Stamp", stampX, stampY, { align: "center" });
  doc.setTextColor(0);
  doc.setDrawColor(0);

  // ACK barcode — bottom-left, fixed distance from the page edge (independent
  // of the flowing content above, which differs in length between copies).
  const barcodeData = generateBarcodeDataUrl(ackNumber);
  if (barcodeData) {
    const H = doc.internal.pageSize.getHeight();
    const bw = 110;
    const bh = 28;
    doc.addImage(barcodeData, "PNG", M, H - bh - 24, bw, bh);
  }
};

// Shared opts builder — everything both PDFs need (candidate/ack/docs/hostel
// details, QR, logo). Split out so Student+Office and Hostel copies can be
// printed as two separate actions instead of always bundled into one PDF.
const buildCommonOpts = async (app) => {
  const logo = await loadLogo();

  // Compose the filtered documents list: only physical documents the roadmin
  // ticked as "Original Received" — policies are digitally signed & uploaded,
  // saadmin never gets this control, so they never appear on this receipt.
  const orig = app.original_received || {};
  const docs = DOCUMENT_TYPES.filter((d) => !!orig[d.id]).map((d) => ({
    label: d.label,
    checked: true, // admin already ticked as received — pre-tick in the copy for clarity
  }));

  // QR encodes the ACK number as JSON.
  const qrPayload = JSON.stringify({ ack_number: app.ack_number || "" });
  let qrDataUrl = null;
  try {
    qrDataUrl = await QRCode.toDataURL(qrPayload, { margin: 1, width: 320 });
  } catch (_e) { /* ignore */ }

  // The date shown on the acknowledgement is today — the day the roadmin actually
  // approved and printed the copy. The scheduled slot date isn't reliable here
  // since a candidate may show up on a different day than originally assigned;
  // approval (and printing) always happens on the real day the originals were
  // physically verified.
  const receivedOn = new Date().toISOString();

  return {
    logo,
    qrDataUrl,
    candidateName: app.candidate?.name || "—",
    applicationNumber: app.application_number || "—",
    rollNumber: app.roll_number || "",
    ackNumber: app.ack_number || "",
    receivedOn,
    docs,
    hostel: app.hostel || {},
    // Registrations now span multiple schools, not just School of Management —
    // resolved server-side from student_category_id -> entity_id -> entities.entity_name
    // (see _resolve_course_info). Falls back to a generic label if unresolved.
    schoolName: app.course_info?.school || null,
  };
};

// Student + Office copies — printed as their own action, independent of the
// Hostel copy (see buildHostelCopyPdf).
export const buildRegistrationCopyPdf = async (app) => {
  const common = await buildCommonOpts(app);
  const doc = new jsPDF({ unit: "pt", format: "a4" });

  await drawCopyPage(doc, { ...common, copyType: "STUDENT COPY" });
  doc.addPage();
  await drawCopyPage(doc, { ...common, copyType: "OFFICE COPY" });

  const blob = doc.output("blob");
  return {
    blob,
    url: URL.createObjectURL(blob),
    fileName: `MU-Registration-Copy-${app.application_number}.pdf`,
  };
};

// Hostel copy — its own separate print action, only relevant for hostelers.
const extractReservationToken = (hostel) => {
  if (!hostel) return null;
  if (hostel.reservation_id) return String(hostel.reservation_id);
  const url = hostel.checkin_url;
  if (url) {
    try {
      const parsed = new URL(url);
      const token = parsed.searchParams.get("token");
      if (token) return token.trim();
    } catch (_e) {
      const match = String(url).match(/[?&]token=([^&]+)/);
      if (match?.[1]) return decodeURIComponent(match[1]).trim();
    }
  }
  return null;
};

const buildHostelCheckinQrDataUrl = async (hostel) => {
  if (!hostel) return null;

  const token = extractReservationToken(hostel);
  if (!token) return null;

  const content = JSON.stringify({ type: "hostel_checkin", token });
  try {
    return await QRCode.toDataURL(content, { margin: 1, width: 320 });
  } catch (_e) {
    return null;
  }
};

export const buildHostelCopyPdf = async (app) => {
  const common = await buildCommonOpts(app);
  const hostelCheckinQrDataUrl = await buildHostelCheckinQrDataUrl(app.hostel);
  const doc = new jsPDF({ unit: "pt", format: "a4" });

  drawHostelTearOffPage(doc, { ...common, hostelCheckinQrDataUrl });

  const blob = doc.output("blob");
  return {
    blob,
    url: URL.createObjectURL(blob),
    fileName: `MU-Hostel-Copy-${app.application_number}.pdf`,
  };
};

// Small receipt-width (80mm thermal) slip handed to a token-mode candidate
// right after check-in — only ever printed from the Check-in Desk login.
// Deliberately its own tiny page size, not A4 — this is meant to print fast
// on a front-desk receipt printer, not be filed away like the other copies.
export const buildTokenSlipPdf = async (app) => {
  const logo = await loadLogo();
  const slot = app.assigned_slot || {};
  const pageW = 226; // 80mm at 72dpi
  const pageH = 320;
  const doc = new jsPDF({ unit: "pt", format: [pageW, pageH] });
  const M = 14;
  const contentW = pageW - 2 * M;
  let y = 8;

  drawLogoAt(doc, logo, pageW / 2 - 46, y, 92, 70);
  y += 66;

  doc.setFont("times", "normal");
  doc.setFontSize(10);
  doc.text("Registrations — 2026", pageW / 2, y, { align: "center" }); y += 14;

  doc.setDrawColor(0x6B, 0x72, 0x80);
  doc.setLineDashPattern([2, 2], 0);
  doc.line(M, y, pageW - M, y);
  doc.setLineDashPattern([], 0);
  y += 20;

  // The token number is the one thing staff/candidate need at a glance —
  // enough vertical gap between the label and the large number so their
  // baselines/ascent don't overlap.
  doc.setFont("times", "bold");
  doc.setFontSize(9);
  doc.text("TOKEN NO.", pageW / 2, y, { align: "center" }); y += 44;
  // Bare number, no group prefix — the TV board only ever announces the
  // bare token number too (each screen is scoped to one group), so this
  // slip matches exactly what gets called out.
  doc.setFontSize(40);
  const tokenLabel = app.token_number != null ? String(app.token_number) : "—";
  doc.text(tokenLabel, pageW / 2, y, { align: "center" }); y += 22;

  doc.setDrawColor(0x6B, 0x72, 0x80);
  doc.setLineDashPattern([2, 2], 0);
  doc.line(M, y, pageW - M, y);
  doc.setLineDashPattern([], 0);
  y += 18;

  // Just the values, centered — no "Name:"/"Application No.:" labels.
  doc.setFont("times", "bold");
  doc.setFontSize(10);
  const nameLines = doc.splitTextToSize(app.candidate?.name || "—", contentW);
  doc.text(nameLines, pageW / 2, y, { align: "center" }); y += nameLines.length * 12;
  doc.setFont("times", "normal");
  doc.setFontSize(9);
  doc.text(app.application_number || "—", pageW / 2, y, { align: "center" }); y += 16;

  const row = (label, value) => {
    doc.setFont("times", "bold");
    doc.setFontSize(8);
    doc.text(label, M, y);
    doc.setFont("times", "normal");
    const lines = doc.splitTextToSize(String(value || "—"), contentW);
    doc.text(lines, M, y + 10);
    y += 10 + lines.length * 10 + 4;
  };

  row("Venue", slot.venue);
  row("Checked Date", formatShortDate(app.checked_in_at || new Date().toISOString()));

  y += 4;
  doc.setFont("times", "italic");
  doc.setFontSize(7);
  doc.setTextColor(0x6B, 0x72, 0x80);
  const note = doc.splitTextToSize("Your actual seat/counter will be called by this token number. Please keep this slip until your registration is complete.", contentW);
  doc.text(note, M, y);
  doc.setTextColor(0);

  const blob = doc.output("blob");
  return {
    blob,
    url: URL.createObjectURL(blob),
    fileName: `MU-Token-${app.application_number}.pdf`,
  };
};

// Small helper to build ONLY a QR data URL, encoding the ACK number as JSON.
export const buildAppQrDataUrl = async (applicationNumber, ackNumber) => {
  try {
    return await QRCode.toDataURL(JSON.stringify({ ack_number: ackNumber || "" }), { margin: 1, width: 180 });
  } catch (_e) { return null; }
};
